import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../../utils/helper';
import type { OoReport, OrderDetails, PatientNoteGroup, RadUser } from '../types';

/**
 * OnlyOffice variant of useReportData.
 *
 * Calls /get-reports-oodocs and /get-prior-reports-oodocs which return
 * reports with a nested `document` object (doc_id, doc_key, etc.).
 * Everything else (notes, users) uses the same endpoints as before.
 */
export const useReportDataOodocs = (orderDetails: OrderDetails | null) => {
  const [reports, setReports] = useState<OoReport[]>([]);
  const [currentReport, setCurrentReport] = useState<OoReport | null>(null);
  const [priorReports, setPriorReports] = useState<OoReport[]>([]);
  const [notes, setNotes] = useState<PatientNoteGroup[]>([]);
  const [radUsers, setRadUsers] = useState<RadUser[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isSignedOff, setIsSignedOff] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // The order whose data the state above currently describes. Every fetch below tags itself
  // with the order it was issued for and drops its response if this has moved on, so a slow
  // reply for a study the queue already left can never overwrite the current one.
  const loadedOrderIdRef = useRef<number | null>(null);

  // The report the USER explicitly opened from Report History. Pinned because the selection
  // rule below always prefers the newest sign-off, and a BACKGROUND refetch (the once-per-doc
  // autosave refresh, a post-template reload) would then re-select it — pulling the editor
  // off a version the user deliberately opened to re-report from, mid-dictation, and forking
  // a second scratch from the newest snapshot in the process. Cleared when the order changes
  // or when the pinned row disappears.
  const pinnedReportIdRef = useRef<number | null>(null);

  // Selecting a report is always an explicit act (Report History click, a freshly forked
  // working copy) — record it as the pin, so later refetches keep it.
  const selectReport = useCallback((report: OoReport | null) => {
    pinnedReportIdRef.current = report?.pr_id ?? null;
    setCurrentReport(report);
  }, []);

  // ── Reports for this order (with document info) ──
  // opts.selectDocId: after seeding a private working copy (or a Draft/Reviewed/prior copy)
  // the caller passes the new doc ID so the freshly created row becomes the working surface
  // (and autosave target).
  const fetchReports = useCallback(async (opts: { selectDocId?: number } = {}) => {
    if (!pacs_order) return;
    const orderId = pacs_order.pacs_ord_id;
    try {
      const res = await makePostCall('/get-reports-oodocs', {
        order_id: orderId,
        user_id: getUserDetails()?.username,
      });
      if (loadedOrderIdRef.current !== orderId) return; // queue advanced while in flight
      const data: OoReport[] = res.data?.data || [];
      setReports(data);

      const hasSignedOff = data.some(r => r.pr_status === 'SIGNEDOFF');
      setIsSignedOff(hasSignedOff);

      if (data.length > 0) {
        setCurrentReport(prev => {
          // An explicit user selection outranks every rule below — including "newest sign-off
          // wins", which is what an order with several sign-offs would otherwise snap back to.
          const pinnedId = pinnedReportIdRef.current;
          if (pinnedId != null) {
            const pinned = data.find(r => r.pr_id === pinnedId);
            if (pinned) return pinned;
            pinnedReportIdRef.current = null; // the pinned row is gone (deleted / superseded)
          }
          // Signed-off order: the newest signed snapshot IS the action context. It opens in
          // edit mode but nothing persists server-side — re-reporting goes through sign-off,
          // which captures the live editor session (even when a template working copy is the
          // surface). (Legacy TinyMCE-era signed rows have no linked document — skip those.)
          const signed = data.find(r => r.pr_status === 'SIGNEDOFF' && r.document?.doc_id);
          if (signed) return signed;
          // Explicit switch to a just-forked copy
          if (opts.selectDocId) {
            const target = data.find(r => r.document?.doc_id === opts.selectDocId);
            if (target) return target;
          }
          // Keep the current selection if its row still exists (status may have changed).
          // This also pins the surface mid-session, so a draft committed by someone else
          // while the user is editing never yanks the editor out from under them — freshness
          // is only re-evaluated on a fresh open (prev === null).
          const stillThere = prev?.pr_id ? data.find(r => r.pr_id === prev.pr_id) : null;
          if (stillThere) return stillThere;

          const isCommitted = (r: OoReport) => !!r.pr_status && r.pr_status !== 'AUTOSAVED';
          const editable = (r: OoReport) => !!r.document?.doc_id && r.document.doc_status !== 'finalized';

          // Prefer this user's own private working copy (AUTOSAVED) — get-reports-oodocs only
          // returns the requester's own AUTOSAVED rows, so any one here belongs to them.
          // (Rows come back newest-first, so find() yields the most recent working copy.)
          const isWorkingCopy = (r: OoReport) =>
            (!r.pr_status || r.pr_status === 'AUTOSAVED') && editable(r);
          const mine = data.find(isWorkingCopy);

          // Freshness check: only RESUME the working copy if it is not stale — i.e. no
          // committed draft/review was made after it (e.g. another user drafted a newer
          // version in the meantime). pr_id is monotonic, so a committed row with a higher
          // pr_id than the copy means the copy predates the latest shared state → re-seed
          // from that newer committed entry instead of resurrecting older content.
          const newestCommittedId = data
            .filter(isCommitted)
            .reduce((max, r) => Math.max(max, r.pr_id ?? 0), 0);
          if (mine && (mine.pr_id ?? 0) > newestCommittedId) return mine;

          // No fresh working copy — select the newest editable committed report as the SEED
          // source. The editor copies it into a fresh private working copy rather than editing
          // it directly (see the open effect in OOReportEditor / SplitScreenEditorOodocs).
          return data.find(r => editable(r) && isCommitted(r)) || data.find(editable) || data[0];
        });
      } else {
        setCurrentReport(null);
      }
      setInitialFetchDone(true);
    } catch (e) {
      console.error('Failed to fetch oodocs reports:', e);
      if (loadedOrderIdRef.current === orderId) setInitialFetchDone(true);
    }
  }, [pacs_order?.pacs_ord_id]);

  // ── Prior reports (other orders for same patient, with document info) ──
  const fetchPriorReports = useCallback(async () => {
    if (!pacs_order || !patient) return;
    const orderId = pacs_order.pacs_ord_id;
    try {
      const res = await makePostCall('/get-prior-reports-oodocs', {
        order_id: orderId,
        yh_no: patient.pat_pin,
      });
      if (loadedOrderIdRef.current !== orderId) return;
      setPriorReports(res.data?.data || []);
    } catch {
      if (loadedOrderIdRef.current === orderId) setPriorReports([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Patient notes / documents (same endpoint as before) ──
  const fetchNotes = useCallback(async () => {
    if (!pacs_order || !patient) return;
    const orderId = pacs_order.pacs_ord_id;
    try {
      const res = await makePostCall('/pat-prev-notes', {
        user_id: getUserDetails()?.username,
        pin: patient.pat_pin,
        order_id: pacs_order.po_his_ord_no,
        ord_no: orderId,
      });
      if (loadedOrderIdRef.current !== orderId) return;
      setNotes(res.data?.data || []);
    } catch {
      if (loadedOrderIdRef.current === orderId) setNotes([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Rad users (for co-sign dropdown) ──
  const fetchRadUsers = useCallback(async () => {
    try {
      const res = await makeGetCall('/get-rad-users');
      setRadUsers(res.data?.data || []);
    } catch {
      setRadUsers([]);
    }
  }, []);

  // ── All users (for proxy draft dropdown) ──
  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await makePostCall('/user-list', {});
      setAllUsers(res.data?.data || []);
    } catch {
      setAllUsers([]);
    }
  }, []);

  // Fetch everything when order changes
  useEffect(() => {
    if (!orderDetails) return;
    const orderId = pacs_order?.pacs_ord_id ?? null;

    // A DIFFERENT study arriving must not leave the previous study's data behind. Until the
    // new fetches land, `reports` / `currentReport` still describe the study that was just
    // signed off and popped from the queue, and both the editor and the commit actions read
    // them as if they belonged to the incoming patient. Clear first, then load.
    // Re-selecting the SAME order (id unchanged) deliberately skips this, so a re-click does
    // not tear down the live editing surface.
    if (loadedOrderIdRef.current !== orderId) {
      loadedOrderIdRef.current = orderId;
      pinnedReportIdRef.current = null;
      setReports([]);
      setCurrentReport(null);
      setPriorReports([]);
      setNotes([]);
      setIsSignedOff(false);
      // Re-arms the editor's "wait for the first fetch" guard, which would otherwise stay
      // satisfied from the first study for the rest of the session.
      setInitialFetchDone(false);
    }

    fetchReports();
    fetchPriorReports();
    fetchNotes();
  }, [orderDetails]);

  // Fetch user lists once on mount
  useEffect(() => {
    fetchRadUsers();
    fetchAllUsers();
  }, []);

  return {
    reports,
    currentReport,
    // Exposed under the plain setter name: every caller means "the user picked this one".
    setCurrentReport: selectReport,
    priorReports,
    notes,
    radUsers,
    allUsers,
    isSignedOff,
    setIsSignedOff,
    initialFetchDone,
    refetchReports: fetchReports,
  };
};
